import { Link } from "react-router";
import { ArrowLeft, FolderPlus, CheckCircle, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function CreateProjectDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <FolderPlus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Create New Project</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              <a
                href="/docs/glossary#:~:text=Project%20Manager"
                className="text-blue-600 hover:underline"
              >
                Projects
              </a>{" "}
              are the top-level organizational structure in Photon Flow. They contain{" "}
              <a href="/docs/glossary#:~:text=Mask" className="text-blue-600 hover:underline">
                masks
              </a>
              ,{" "}
              <a
                href="/docs/glossary#:~:text=Test%20Label"
                className="text-blue-600 hover:underline"
              >
                test labels
              </a>
              , measurement requests, and project specifications. Creating a new project is the
              first step in setting up your testing workflow.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>
                  • Your role is{" "}
                  <a
                    href="/docs/glossary#:~:text=Project%20Manager"
                    className="text-blue-600 hover:underline"
                  >
                    project manager
                  </a>{" "}
                  or Admin
                </li>
                <li>• Project name</li>
                <li>• WBS</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Navigate to Projects Page</h3>
                <p className="text-gray-700 mb-2">
                  From the main navigation or the landing page, click on "Projects" to access the
                  projects overview.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Go to Projects</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Click "Create New Project"</h3>
                <p className="text-gray-700">
                  Look for the "Create New Project" button and click it to open the project creation
                  dialog.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Fill in Project Details</h3>
                <p className="text-gray-700 mb-2">
                  Complete the project creation form with the following information:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Project Name:</strong> A clear, descriptive name for your project
                  </li>
                  <li>
                    • <strong>Description (optional):</strong> Optional detailed description of the
                    project's purpose
                  </li>
                  <li>
                    • <strong>Start date (optional):</strong> The date when the project is expected
                    to start
                  </li>
                  <li>
                    • <strong>End date (optional):</strong> The date when the project is expected to
                    end
                  </li>
                  <li>
                    • <strong>Funding:</strong> Defaults to unknown. Can be MPW, EU Horizon,
                    internal, customer
                  </li>
                  <li>
                    • <strong>WBS:</strong>WBS number that can be used to link all associated costs
                  </li>
                  <li>
                    • <strong>Learning goal (optional):</strong> short description of the main
                    learning goal of this project
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Save and Create</h3>
                <p className="text-gray-700">
                  Click "Create" or "Save" to create your new project. You'll be redirected to the
                  project detail page where you can start adding masks and other components.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              A project can be edited at any time to update its details by clicking the{" "}
              <Edit className="inline-block w-4 h-4 ml-1" /> edit button.
            </p>
            <p className="text-gray-700 mb-4">After creating your project, you can:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/mask-workflow">Continue to the Mask Workflow</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
